import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { CreatePieceTypeRequest, PieceType, UpdatePieceTypeRequest } from "@/api/types";
import { usePieceLinkOptions } from "@/hooks/usePieceTypes";
import { DialogShell } from "./DialogShell";
import "./EmployeeFormDialog.css";
import "./PieceTypeDialog.css";

const MAX_NAME = 80;
const MAX_UNIT = 20;
const DEFAULT_UNIT = "head";
const RATE_PATTERN = /^\d+(\.\d{1,4})?$/;
const MAX_RATE = 10000;

/** Code points, not UTF-16 units: an 80-emoji name is 80 characters to the server. */
function charCount(s: string): number {
  return Array.from(s).length;
}

/** A rate as typed for editing: at least two decimals, at most four, no trailing noise. */
export function rateInputText(rate: number): string {
  const four = rate.toFixed(4);
  return four.endsWith("00") ? four.slice(0, -2) : four.replace(/0$/, "");
}

/** `herd:<id>`, `group:<id>` or "" (not linked) — one select carries both kinds of link. */
function linkValue(t: PieceType | null): string {
  if (t?.herdId) return `herd:${t.herdId}`;
  if (t?.groupId) return `group:${t.groupId}`;
  return "";
}

/**
 * ef-060-6 — add or edit a piece type: Name, Unit, Rate per unit, and an optional herd or group the
 * head count comes from. Same shell as the other People-tab dialogs: mounts per open, focus trap,
 * Escape/overlay/Cancel close, the caller returns focus to its trigger.
 *
 * ONE `role="alert"` carries every refusal (inline rate check, the server's 400/409) so a screen
 * reader hears a single announcement; the offending field gets `aria-invalid`. A rate edit changes
 * FUTURE tickets only — the server snapshots the rate on each ticket.
 */
export function PieceTypeDialog({
  pieceType,
  onClose,
  onSaved,
}: {
  /** `null` = add. */
  pieceType: PieceType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(pieceType?.name ?? "");
  const [unit, setUnit] = useState(pieceType?.unit ?? DEFAULT_UNIT);
  const [rate, setRate] = useState(pieceType ? rateInputText(pieceType.rate) : "");
  const initialLink = linkValue(pieceType);
  const [link, setLink] = useState(initialLink);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());

  const options = usePieceLinkOptions();
  const alertId = useId();
  const nameId = useId();
  const unitId = useId();
  const rateId = useId();
  const linkId = useId();
  const rateHintId = useId();
  const formId = useId();
  const inFlight = useRef(false);

  function refuse(field: string, message: string): void {
    setInvalid(new Set([field]));
    setError(message);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return; // a double-click must not send a second request
    setError(null);
    setInvalid(new Set());

    const trimmedName = name.trim();
    const trimmedUnit = unit.trim();
    const trimmedRate = rate.trim();

    if (trimmedName === "" || charCount(trimmedName) > MAX_NAME) {
      return refuse("name", `Name: enter 1 to ${MAX_NAME} characters.`);
    }
    if (trimmedUnit === "" || charCount(trimmedUnit) > MAX_UNIT) {
      return refuse("unit", `Unit: enter 1 to ${MAX_UNIT} characters, like head or lb.`);
    }
    if (trimmedRate.includes(",")) {
      return refuse("rate", "Rate per unit: use a point, like 0.50 — not a comma.");
    }
    const rateNumber = Number(trimmedRate);
    if (!RATE_PATTERN.test(trimmedRate) || !(rateNumber > 0) || rateNumber > MAX_RATE) {
      return refuse(
        "rate",
        "Rate per unit: enter an amount above 0 and up to 10,000, with at most four decimals.",
      );
    }

    inFlight.current = true;
    setSaving(true);
    try {
      const [kind, id] = link === "" ? ["", ""] : (link.split(":") as [string, string]);
      if (pieceType) {
        const body: UpdatePieceTypeRequest = { name: trimmedName, unit: trimmedUnit, rate: rateNumber };
        if (link !== initialLink) {
          if (link === "") body.unlinkLivestock = true;
          else if (kind === "herd") body.herdId = id;
          else body.groupId = id;
        }
        await demoApi.updatePieceType(pieceType.id, body);
      } else {
        const body: CreatePieceTypeRequest = { name: trimmedName, unit: trimmedUnit, rate: rateNumber };
        if (kind === "herd") body.herdId = id;
        else if (kind === "group") body.groupId = id;
        await demoApi.createPieceType(body);
      }
      onSaved();
      onClose();
    } catch (err) {
      const fields = problemFieldErrors(err);
      const entries = Object.entries(fields ?? {}).filter(([, msgs]) => msgs.length > 0);
      if (entries.length > 0) {
        // The server names fields `rate`, `herdId`...; the select carries both herd and group.
        const keys = entries.map(([key]) => (/^(herd|group)id$/i.test(key) ? "link" : key.toLowerCase()));
        setInvalid(new Set(keys));
        setError(entries.map(([key, msgs]) => `${key}: ${msgs[0] ?? ""}`).join(" "));
      } else {
        const message = problemMessage(err, "Could not save this piece type.");
        setError(message);
        if (/name/i.test(message)) setInvalid(new Set(["name"]));
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const describe = (field: string): string | undefined => (invalid.has(field) ? alertId : undefined);
  const flag = (field: string): true | undefined => (invalid.has(field) ? true : undefined);

  // The current link may be an archived herd/group that the active lists no longer carry; keep it
  // selectable so an unrelated edit does not look like it is about to drop the link.
  const knownLinks = new Set([
    ...options.herds.map((h) => `herd:${h.id}`),
    ...options.groups.map((g) => `group:${g.id}`),
  ]);
  const orphanLabel = pieceType?.herdName ?? pieceType?.groupName ?? null;
  const showOrphan = initialLink !== "" && !knownLinks.has(initialLink) && orphanLabel !== null;

  return (
    <DialogShell
      title={pieceType ? "Edit piece type" : "Add piece type"}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="emp-dialog__field">
          <label htmlFor={nameId}>Name</label>
          <input
            id={nameId}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            aria-invalid={flag("name")}
            aria-describedby={describe("name")}
          />
        </div>

        <div className="piece-type-dialog__row">
          <div className="emp-dialog__field">
            <label htmlFor={unitId}>Unit</label>
            <input
              id={unitId}
              type="text"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("unit")}
              aria-describedby={describe("unit")}
            />
          </div>
          <div className="emp-dialog__field">
            <label htmlFor={rateId}>Rate per unit ($)</label>
            <input
              id={rateId}
              type="text"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("rate")}
              aria-describedby={[rateHintId, describe("rate")].filter(Boolean).join(" ")}
            />
          </div>
        </div>
        <p id={rateHintId} className="emp-dialog__hint">
          Up to four decimals (0.0625). A rate change applies to new tickets only.
        </p>

        <div className="emp-dialog__field">
          <label htmlFor={linkId}>Head count from</label>
          <select
            id={linkId}
            value={link}
            onChange={(e) => setLink(e.target.value)}
            aria-invalid={flag("link")}
            aria-describedby={describe("link")}
          >
            <option value="">Not linked (type the count by hand)</option>
            {showOrphan && <option value={initialLink}>{orphanLabel}</option>}
            {options.herds.length > 0 && (
              <optgroup label="Herds">
                {options.herds.map((h) => (
                  <option key={h.id} value={`herd:${h.id}`}>
                    {h.name}
                  </option>
                ))}
              </optgroup>
            )}
            {options.groups.length > 0 && (
              <optgroup label="Groups">
                {options.groups.map((g) => (
                  <option key={g.id} value={`group:${g.id}`}>
                    {g.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {error && (
          <div id={alertId} role="alert">
            <p className="emp-dialog__error">{error}</p>
          </div>
        )}

      </form>
    </DialogShell>
  );
}
